import React, { useEffect, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import CollapsibleCart from 'components/common/receipt/collapsible_cart';
import DiscountReceiptItems from 'components/common/receipt/discount_receipt_items';
import ReceiptItem from 'components/common/receipt/receipt_item';
import TaxReceiptItem from 'components/common/receipt/tax_receipt_item';
import { fetchLocalizedComponent } from 'utilities/component_localization';
import { toDinero, toCurrencyWithMinorUnitAmount } from 'utilities/currency';
import { CurrencyCode } from 'rpc/model/squareup/common/currency_codes';
import {
  sumSoftwarePricing,
  sortProductByHardwareOrSoftware,
} from 'utilities/software';
import { useLocation } from 'react-router-dom';

const InstallmentReceipt = fetchLocalizedComponent(
  'common/receipt/installment_receipt',
);

export const orderSummaryStyles = makeStyles((theme) => ({
  orderSummary: {
    border: `1px solid ${theme.color.border}`,
    borderRadius: theme.border.radius.large,
    boxSizing: 'border-box',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    '&:not(:last-child)': {
      borderRadius: `${theme.border.radius.large} ${theme.border.radius.large} 0 0`,
      borderBottom: 'none',
    },
    '&:not(:first-child)': {
      borderTop: 'none',
      borderRadius: `0 0 ${theme.border.radius.large} ${theme.border.radius.large}`,
    },
    '&:not(:first-child):not(:last-child)': {
      borderRadius: '0 0 0 0',
      borderBottom: 'none',
      borderTop: 'none',
      background: theme.color.gray['5'],
    },
  },
}));

const OrderSummary = () => {
  const classes = orderSummaryStyles();
  const { t, i18n } = useTranslation();
  const { orderData } = useCheckoutFlow();

  const { software: softwareProducts, hardware: hardwareProducts } =
    sortProductByHardwareOrSoftware(orderData.cart_summary);

  const { amount: softwareCostTotal, currencyCode: softwareCurrency } =
    sumSoftwarePricing(
      softwareProducts
        .map((product) => product.plan)
        .filter((product) => product !== null),
    );

  useEffect(() => {
    if (i18n.exists('checkout.orderSummary.disclaimer') && Sq.SqShopFooter) {
      Sq.SqShopFooter.disclaimers = [
        t('checkout.orderSummary.disclaimer'),
        ...Sq.SqShopFooter.disclaimers,
      ];
    }
  }, []);

  const isInstallment =
    orderData.payment_due.payment_schedule === 'installment';

  const currentRoute = useLocation().pathname;
  const issuerInstallmentsPayments =
    orderData.issuer_installments_summary?.number_of_payments_selected;
  const showIssuerInstallmentsNumber =
    issuerInstallmentsPayments > 1 &&
    ['/review', '/complete'].includes(currentRoute);

  const isWarranty = orderData.is_warranty;

  const totalAmount = () => {
    if (orderData.completed || !isInstallment || isWarranty) {
      return orderData.total.amount;
    }
    return toDinero(orderData.total.amount)
      .add(toDinero(orderData.installment_summary.fee_amount.amount))
      .toUnit();
  };

  return (
    <div>
      <Grid container direction="column" className={classes.orderSummary}>
        <CollapsibleCart
          title={
            isWarranty
              ? t('checkout.receipt.label.exchange')
              : t('common.hardware').toUpperCase()
          }
          cart={hardwareProducts}
        />
        <Divider sx={{ my: 2 }} />
        <ReceiptItem
          label={orderData.subtotal.label}
          cost={orderData.subtotal.amount}
          currency={orderData.subtotal.currency}
        />
        <ReceiptItem
          label={orderData.shipping.label}
          cost={orderData.shipping.amount}
          currency={orderData.shipping.currency}
          isShipping={true}
        />
        <TaxReceiptItem orderData={orderData} />
        {isInstallment &&
          orderData.installment_summary.fee_amount.amount > 0 && (
            <ReceiptItem
              label="checkout.receipt.label.financingCharge"
              cost={orderData.installment_summary.fee_amount.amount}
              currency={orderData.installment_summary.fee_amount.currency_code}
            />
          )}
        <DiscountReceiptItems orderData={orderData} />
        <Divider sx={{ my: 2 }} />
        <ReceiptItem
          label={
            isWarranty
              ? 'checkout.receipt.label.exchangeValue'
              : orderData.total.label
          }
          cost={totalAmount()}
          currency={orderData.total.currency}
          emphasize
        />
        {/* only display installments if multiple payments selected */}
        {showIssuerInstallmentsNumber && (
          <p>
            {t('checkout.receipt.label.issuerInstallmentsLength', {
              length: issuerInstallmentsPayments,
            })}
          </p>
        )}
        {isWarranty && (
          <ReceiptItem
            label="checkout.receipt.label.dueToday"
            cost={0}
            currency={orderData.total.currency}
            emphasize
          />
        )}
      </Grid>
      <Suspense fallback={null}>
        {Object.entries(orderData.installment_summary).length > 0 && (
          <InstallmentReceipt />
        )}
      </Suspense>
      {softwareProducts.length > 0 && (
        <>
          {Object.keys(orderData.installment_summary).length === 0 && (
            <Divider />
          )}
          <Grid container direction="column" className={classes.orderSummary}>
            <CollapsibleCart
              // TODO: figure out what to do with warranty
              title={
                isWarranty
                  ? t('checkout.receipt.label.exchange')
                  : t('common.software').toUpperCase()
              }
              cart={softwareProducts}
            />
            <Divider sx={{ my: 2 }} />
            <ReceiptItem
              label={t('checkout.receipt.label.estimatedMonthlyBill')}
              override={toCurrencyWithMinorUnitAmount(
                softwareCostTotal,
                CurrencyCode[softwareCurrency],
              )}
              emphasize
            />
            <Box mt={2}>
              <p className="paragraph-20">
                {t('checkout.receipt.label.softwareBillingDisclaimer')}
              </p>
            </Box>
          </Grid>
        </>
      )}
    </div>
  );
};

export default OrderSummary;
